import React, { useState, useEffect } from 'react';
import {
  Save,
  Building2,
  MapPin,
  Warehouse,
  FolderTree,
  Globe,
  Mail,
  Link as LinkIcon,
  Settings,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { coreAPI } from '../../lib/api';
import type { Company } from '../../lib/api/types';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { cn } from '../../lib/utils';
import { OrgChartNode, NodeType, NODE_COLORS, NODE_TYPE_LABELS } from './types';

interface NodeConfigPanelProps {
  node: OrgChartNode | null;
  parentNode?: OrgChartNode | null;
  companyType?: 'supplier' | 'customer';
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const NODE_ICONS: Record<NodeType, React.ElementType> = {
  group: FolderTree,
  division: Building2,
  company: MapPin,
  warehouse: Warehouse,
  location: MapPin,
};

// Currency options
const CURRENCY_OPTIONS = [
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'CHF', label: 'CHF - Swiss Franc' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
];

// Timezone options
const TIMEZONE_OPTIONS = [
  { value: 'Europe/London', label: 'Europe/London (GMT)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET)' },
  { value: 'America/New_York', label: 'America/New_York (EST)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST)' },
];

// Date format options
const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (UK)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (EU)' },
];

// Portal mode options
const PORTAL_MODE_OPTIONS = [
  { value: 'supplier', label: 'Supplier Portal' },
  { value: 'customer', label: 'Customer Portal' },
  { value: 'both', label: 'Both Portals' },
];

export function NodeConfigPanel({
  node,
  parentNode,
  companyType: defaultCompanyType,
  isOpen,
  onClose,
  onSave,
}: NodeConfigPanelProps) {
  // Form state
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [website, setWebsite] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [portalMode, setPortalMode] = useState<string>('');
  const [isActive, setIsActive] = useState(true);

  // Regional settings
  const [currency, setCurrency] = useState('inherit');
  const [timezone, setTimezone] = useState('inherit');
  const [dateFormat, setDateFormat] = useState('inherit');

  const [saving, setSaving] = useState(false);

  // Populate form when node changes
  useEffect(() => {
    if (node) {
      setName(node.name || '');
      setCode(node.code || '');
      setDescription(node.description || '');
      setCity(node.metadata?.city || '');
      setCountry(node.metadata?.country || node.metadata?.headquarters_country || '');
      setWebsite(node.metadata?.website || '');
      setPrimaryEmail(node.metadata?.primary_email || '');
      setPortalMode(node.portal_mode || node.company_type || '');
      setIsActive(node.is_active);

      // Regional settings
      setCurrency(node.regional_settings?.currency || 'inherit');
      setTimezone(node.regional_settings?.timezone || 'inherit');
      setDateFormat(node.regional_settings?.date_format || 'inherit');
    }
  }, [node]);

  const isCreateMode = node?.entity_id === 0;

  const handleSave = async () => {
    if (!node) return;
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (isCreateMode && node.type === 'company' && !primaryEmail.trim()) {
      toast.error('Primary email is required');
      return;
    }

    setSaving(true);
    try {
      if (isCreateMode) {
        // CREATE mode — call create APIs
        if (node.type === 'group') {
          await coreAPI.createCompanyGroup({
            name,
            description,
            headquarters_country: country || undefined,
            website: website || undefined,
            parent_group: parentNode?.entity_id || undefined,
            is_active: isActive,
          });
        } else if (node.type === 'company') {
          await coreAPI.createCompany({
            name,
            city: city || undefined,
            country: country || undefined,
            website: website || undefined,
            primary_email: primaryEmail || undefined,
            portal_mode: (portalMode || undefined) as Company['portal_mode'],
            company_type: defaultCompanyType || 'supplier',
            group: parentNode?.entity_id || undefined,
            is_active: isActive,
          } as Partial<Company>);
        } else if (node.type === 'warehouse') {
          await coreAPI.createWarehouse({
            name,
            code: code || undefined,
            city: city || undefined,
            country: country || undefined,
            site: parentNode?.entity_id || undefined,
            is_active: isActive,
          });
        }
        toast.success(`${NODE_TYPE_LABELS[node.type]} created successfully`);
      } else {
        // UPDATE mode — call update APIs
        const entityId = node.entity_id;

        if (node.type === 'group') {
          await coreAPI.updateCompanyGroup(entityId, {
            name,
            description,
            headquarters_country: country,
            website,
            is_active: isActive,
          });
        } else if (node.type === 'company') {
          await coreAPI.updateCompany(entityId, {
            name,
            city,
            country,
            website,
            primary_email: primaryEmail,
            portal_mode: (portalMode || undefined) as Company['portal_mode'],
            is_active: isActive,
          });
        } else if (node.type === 'warehouse') {
          await coreAPI.updateWarehouse(entityId, {
            name,
            code,
            city,
            country,
            is_active: isActive,
          });
        }
        toast.success(`${NODE_TYPE_LABELS[node.type]} updated successfully`);
      }

      // Save regional settings if any were changed from defaults
      const hasRegionalChanges =
        currency !== 'inherit' || timezone !== 'inherit' || dateFormat !== 'inherit' ||
        (node.regional_settings?.currency && currency === 'inherit') ||
        (node.regional_settings?.timezone && timezone === 'inherit') ||
        (node.regional_settings?.date_format && dateFormat === 'inherit');

      if (hasRegionalChanges) {
        const regionalData: Record<string, string | null> = {
          currency: currency === 'inherit' ? null : currency,
          timezone: timezone === 'inherit' ? null : timezone,
          date_format: dateFormat === 'inherit' ? null : dateFormat,
        };

        if (node.type === 'group') {
          const groupId = isCreateMode ? undefined : node.entity_id;
          if (groupId) {
            await coreAPI.updateCompanyGroupPreferences(groupId, regionalData);
          }
        } else if (node.type === 'company') {
          const companyId = isCreateMode ? undefined : node.entity_id;
          if (companyId) {
            await coreAPI.patchCompany(companyId, regionalData);
          }
        }
      }

      onSave();
      onClose();
    } catch (error) {
      console.error(`Failed to ${isCreateMode ? 'create' : 'update'}:`, error);
      toast.error(getApiErrorMessage(error, `Failed to ${isCreateMode ? 'create' : 'update'}. Please try again.`));
    } finally {
      setSaving(false);
    }
  };

  if (!node) return null;

  const colors = NODE_COLORS[node.type];
  const Icon = NODE_ICONS[node.type];
  const label = NODE_TYPE_LABELS[node.type];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg', colors.bg)}>
              <Icon className={cn('h-5 w-5', colors.text)} />
            </div>
            <div>
              <Badge className={cn('text-xs text-white mb-1', colors.badge)}>
                {label}
              </Badge>
              <DialogTitle className="text-xl">{isCreateMode ? `New ${label}` : node.name}</DialogTitle>
            </div>
          </div>
          <DialogDescription>
            {isCreateMode
              ? `Create a new ${label.toLowerCase()}${parentNode ? ` under ${parentNode.name}` : ''}`
              : `Edit the details for this ${label.toLowerCase()}`}
          </DialogDescription>
        </DialogHeader>

        {/* Form Content */}
        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
              Basic Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={`${label} name`}
                />
              </div>

              {node.type === 'company' && (
                <div>
                  <Label htmlFor="code">Code</Label>
                  <Input
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="e.g., UK-LDN-HQ"
                  />
                </div>
              )}

              {node.type === 'group' && (
                <div className="col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="border-t" />

          {/* Location */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Location
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                />
              </div>
              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="Country"
                />
              </div>
            </div>
          </div>

          {/* Contact Info (for groups and companies) */}
          {(node.type === 'group' || node.type === 'company') && (
            <>
              <div className="border-t" />
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Contact Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  {node.type === 'company' && (
                    <div>
                      <Label htmlFor="email" className="flex items-center gap-2">
                        <Mail className="h-3 w-3" /> Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={primaryEmail}
                        onChange={(e) => setPrimaryEmail(e.target.value)}
                        placeholder="contact@company.com"
                      />
                    </div>
                  )}

                  <div className={node.type === 'group' ? 'col-span-2' : ''}>
                    <Label htmlFor="website" className="flex items-center gap-2">
                      <LinkIcon className="h-3 w-3" /> Website
                    </Label>
                    <Input
                      id="website"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      placeholder="https://www.example.com"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Portal Mode (for companies only) */}
          {node.type === 'company' && (
            <>
              <div className="border-t" />
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Portal Configuration
                </h3>

                <div>
                  <Label htmlFor="portalMode">Portal Mode</Label>
                  <Select value={portalMode} onValueChange={setPortalMode}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select portal mode" />
                    </SelectTrigger>
                    <SelectContent>
                      {PORTAL_MODE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Determines which portal this division can access
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Regional Settings */}
          <div className="border-t" />
          <div className="space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Regional Settings
            </h3>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="currency">Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger>
                    <SelectValue placeholder="Inherit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">Inherit from parent</SelectItem>
                    {CURRENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="timezone">Timezone</Label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger>
                    <SelectValue placeholder="Inherit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">Inherit from parent</SelectItem>
                    {TIMEZONE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="dateFormat">Date Format</Label>
                <Select value={dateFormat} onValueChange={setDateFormat}>
                  <SelectTrigger>
                    <SelectValue placeholder="Inherit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">Inherit from parent</SelectItem>
                    {DATE_FORMAT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="border-t" />
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="isActive" className="text-base">Active</Label>
              <p className="text-xs text-muted-foreground">
                Inactive items are hidden from most views
              </p>
            </div>
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : isCreateMode ? `Create ${label}` : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default NodeConfigPanel;
